import {lazy, FC, Suspense} from 'react'
import {Route, Routes, Navigate} from 'react-router-dom'
import {MasterLayout} from '../../_metronic/layout/MasterLayout'
import TopBarProgress from 'react-topbar-progress-indicator'
import {getCSSVariableValue} from '../../_metronic/assets/ts/_utils'
import {WithChildren} from '../../_metronic/helpers'
import TelaInicial from '../modules/telaInicial/TelaInicial' // Adjust import as necessary
import {useAuth, UserRole} from '../modules/auth'

// Lazy-loaded components
const Cadastros = lazy(() => import('../modules/cadastros/Cadastros'))
const DynamicForm = lazy(() => import('../modules/formulario/DynamicForm'))
const Relatorios = lazy(() => import('../modules/relatorios/Relatorios'))
const Articulador = lazy(() => import('../modules/articulador/Articulador'))
const Consultor = lazy(() => import('../modules/consultor/Consultor'))
const Revisor = lazy(() => import('../modules/revisor/Revisor'))
const EncaminhamentoConsultor = lazy(
  () => import('../modules/encaminhamento/EncaminhamentoConsultor')
)
const EncaminhamentoRevisor = lazy(() => import('../modules/encaminhamento/EncaminhamentoRevisor'))
const Professor = lazy(() => import('../modules/Professor/Professor'))
const ProfessorForm = lazy(() => import('../modules/Professor/ProfessorForm'))
const Focal = lazy(() => import('../modules/focal/Focal'))
const FocalForm = lazy(() => import('../modules/encaminhamento/EncaminhamentoFocal'))
const Casos = lazy(() => import('../modules/casos/Casos'))

const PrivateRoutes = () => {
  const {currentUser} = useAuth()

  const routeGroups = {
    home: [
      <Route key='home' path='/home' element={<SuspensedView><TelaInicial /></SuspensedView>} />,
    ],
    cadastros: [
      <Route key='cadastros' path='/cadastros/*' element={<SuspensedView><Cadastros /></SuspensedView>} />,
    ],
    articulador: [
      <Route key='articulador' path='/articulador' element={<SuspensedView><Articulador /></SuspensedView>} />,
      <Route key='formulario' path='/formulario' element={<SuspensedView><DynamicForm /></SuspensedView>} />,
    ],
    consultor: [
      <Route key='consultor' path='/consultor' element={<SuspensedView><Consultor /></SuspensedView>} />,
      <Route key='encaminhamento-consultor' path='/encaminhamento/consultor/:id' element={<SuspensedView><EncaminhamentoConsultor /></SuspensedView>} />,
    ],
    revisor: [
      <Route key='revisor' path='/revisor' element={<SuspensedView><Revisor /></SuspensedView>} />,
      <Route key='encaminhamento-revisor' path='/encaminhamento/revisor/:id' element={<SuspensedView><EncaminhamentoRevisor /></SuspensedView>} />,
    ],
    professor: [
      <Route key='professor' path='/professor' element={<SuspensedView><Professor /></SuspensedView>} />,
      <Route key='professor-form' path='/professor-form' element={<SuspensedView><ProfessorForm /></SuspensedView>} />,
      <Route key='professor-form-id' path='/professor-form/:id' element={<SuspensedView><ProfessorForm /></SuspensedView>} />,
      <Route key='casos' path='/casos/:id' element={<SuspensedView><Casos /></SuspensedView>} />,
    ],
    focal: [
      <Route key='focal' path='/focal' element={<SuspensedView><Focal /></SuspensedView>} />,
      <Route key='encaminhamento-focal' path='/encaminhamento/focal/:id' element={<SuspensedView><FocalForm /></SuspensedView>} />,
    ],
    relatorio: [
      <Route key='relatorio' path='/relatorio' element={<SuspensedView><Relatorios /></SuspensedView>} />,
    ]
  };

  // Map roles to allowed routes using groups
  const routesByRole: Record<UserRole, JSX.Element[]> = {
    [UserRole.ADMINISTRATOR]: [
      ...routeGroups.home, ...routeGroups.cadastros, ...routeGroups.articulador, 
      ...routeGroups.consultor, ...routeGroups.revisor, ...routeGroups.professor, 
      ...routeGroups.focal, ...routeGroups.relatorio
    ],
    [UserRole.MANAGER]: [
      ...routeGroups.home, ...routeGroups.articulador, ...routeGroups.consultor, 
      ...routeGroups.revisor, ...routeGroups.professor, ...routeGroups.focal, 
      ...routeGroups.relatorio
    ],
    [UserRole.ARTICULATOR]: [
      ...routeGroups.home, ...routeGroups.articulador, ...routeGroups.professor, 
      ...routeGroups.focal, ...routeGroups.relatorio
    ],
    [UserRole.CONSULTOR]: [
      ...routeGroups.home, ...routeGroups.articulador, ...routeGroups.consultor, 
      ...routeGroups.focal, ...routeGroups.relatorio
    ],
    [UserRole.REVIEWER]: [
      ...routeGroups.home, ...routeGroups.articulador, ...routeGroups.consultor, 
      ...routeGroups.revisor, ...routeGroups.focal, ...routeGroups.relatorio
    ],
    [UserRole.TEACHER]: [
      ...routeGroups.home, ...routeGroups.professor
    ],
    [UserRole.FOCAL]: [
      ...routeGroups.home, ...routeGroups.focal, ...routeGroups.relatorio
    ],
  }

  // Fallback route if no role matches
  // Fallback route if no role matches
  const getRoutesForRole = (roles: UserRole[]) => {
    // Check which roles are in the user's roles and return all relevant routes

    // Ensure roles is an array before calling reduce
    if (!Array.isArray(roles)) {
      return [] // If roles is not an array, return an empty array
    }
    const allRoutes = roles.reduce<JSX.Element[]>((routes, role) => {
      return routes.concat(routesByRole[role] || [])
    }, [])

    // Deduplicate routes by key to avoid rendering multiple identical routes for users with multiple roles
    const uniqueRoutes = allRoutes.filter(
      (route, index, self) => index === self.findIndex((t) => t.key === route.key)
    )

    return uniqueRoutes
  }

  const getDefaultRoute = (roles: UserRole[] | undefined) => {
    if (!roles || roles.length === 0) return '/home'
    const role = roles[0]

    switch (role) {
      case UserRole.ADMINISTRATOR:
        return '/home'
      case UserRole.MANAGER:
      case UserRole.CONSULTOR:
      case UserRole.REVIEWER:
      case UserRole.ARTICULATOR:
        return '/articulador'
      case UserRole.TEACHER:
        return '/professor'
      default:
        return '/home'
    }
  }

  return (
    <Routes>
      <Route element={<MasterLayout />}>
        {/* Redirect to default route after successful login/registration */}
        <Route path='auth/*' element={<Navigate to={getDefaultRoute(currentUser?.role)} />} />

        {/* Render role-specific routes */}
        {currentUser?.role ? getRoutesForRole(currentUser.role) : null}

        {/* Other Routes */}

        {/* Page Not Found */}
        <Route path='*' element={<Navigate to='/error/404' />} />
      </Route>
    </Routes>
  )
}

const SuspensedView: FC<WithChildren> = ({children}) => {
  const baseColor = getCSSVariableValue('--bs-primary')
  TopBarProgress.config({
    barColors: {
      '0': baseColor,
    },
    barThickness: 1,
    shadowBlur: 5,
  })
  return <Suspense fallback={<TopBarProgress />}>{children}</Suspense>
}

export {PrivateRoutes}
